// "Support the movement" chooser, rebuilt from the English donate page on the
// Wix site (standing-together.org/en/donate-en-3): a row of currency tiles,
// then two cards — monthly ("Become a Friend", Friends of Standing Together)
// and one-time. It only collects the two choices and hands them to `onSelect`;
// what happens next (open a checkout, switch a tab) is the caller's business.
//
// The currency is controlled by the caller, because its default comes from the
// visitor's country and arrives after the first render. The caller always has
// one selected (USD when the country is unknown), so a card never has to ask.
//
// English and LTR on purpose, like the page it copies — the rest of this app is
// RTL Hebrew/Arabic, so the root sets its own dir/lang.

// The six main currencies, in the Wix page's order. Every one is also in
// CURRENCY_ORDER (currencies.js), so a code picked here is one checkout accepts.
// The rest are behind the "Other" tile, which opens the form with its currency
// dropdown (the client dropped CHF and BRL tiles for it).
export const SUPPORT_CURRENCIES = ['USD', 'GBP', 'CAD', 'EUR', 'ILS', 'AUD'];

const FREQUENCIES = [
  {
    recurring: true,
    icon: '/images/fost-logo.png',
    title: 'Become a Friend',
    sub: 'And support us with a monthly donation',
  },
  {
    recurring: false,
    icon: '/images/donate-heart.png',
    title: 'One-Time Donation',
    sub: 'Make an immediate impact',
  },
];

export default function SupportTheMovement({
  currencies = SUPPORT_CURRENCIES,
  currency,
  onCurrencyChange,
  onSelect,
  otherWaysHref,
  onOtherWays,
  // The frequency whose form is open below (true = monthly, false = one-time),
  // highlighted as on Wix; null/undefined when none is.
  selected = null,
}) {
  const pickCurrency = (code) => {
    if (onCurrencyChange) onCurrencyChange(code);
  };

  const pickFrequency = (recurring) => {
    if (onSelect) onSelect({ currency, recurring });
  };

  return (
    <section className="stm" dir="ltr" lang="en">
      <img className="stm-logo" src="/images/logo-en-purple.png" alt="Standing Together" />
      <h1 className="stm-title">Support the movement</h1>

      <h2 className="stm-step">1. Choose a currency</h2>
      <div className="stm-currencies" role="radiogroup" aria-label="Currency">
        {currencies.map((code) => (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={currency === code}
            className={`stm-currency${currency === code ? ' is-active' : ''}`}
            onClick={() => pickCurrency(code)}
          >
            <img className="stm-flag" src={`/images/flags/${code.toLowerCase()}.svg`} alt="" />
            <span>{code}</span>
          </button>
        ))}
        {/* Selected for any currency without a tile of its own. Picking it
            sends null — "not chosen yet" — unless one of those is already
            chosen, in which case it stays. */}
        <button
          type="button"
          role="radio"
          aria-checked={!currencies.includes(currency)}
          className={`stm-currency${!currencies.includes(currency) ? ' is-active' : ''}`}
          onClick={() => { if (currencies.includes(currency)) pickCurrency(null); }}
        >
          <svg className="stm-flag stm-flag--other" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M2 12h20M12 2c3 3 4 6.5 4 10s-1 7-4 10M12 2c-3 3-4 6.5-4 10s1 7 4 10" />
          </svg>
          <span>Other</span>
        </button>
      </div>

      <h2 className="stm-step">2. Choose a frequency</h2>
      <div className="stm-frequencies">
        {FREQUENCIES.map((f) => (
          <button
            key={f.title}
            type="button"
            className={`stm-card${selected === f.recurring ? ' is-active' : ''}`}
            aria-pressed={selected === f.recurring}
            onClick={() => pickFrequency(f.recurring)}
          >
            <img className="stm-card-icon" src={f.icon} alt="" />
            <span className="stm-card-title">{f.title}</span>
            <span className="stm-card-sub">{f.sub}</span>
          </button>
        ))}
      </div>

      {(otherWaysHref || onOtherWays) && (
        <a
          className="stm-other"
          href={otherWaysHref || '#'}
          onClick={onOtherWays && ((e) => {
            if (!otherWaysHref) e.preventDefault();
            onOtherWays(e);
          })}
        >
          Click here to make donations via Check/DAF/Wire transfer
        </a>
      )}
    </section>
  );
}
